import { CloudUploadOutlined, DownloadOutlined, EditOutlined, ExclamationOutlined, HourglassOutlined, LoadingOutlined, RedoOutlined, StopOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, message, Popconfirm, Space } from 'antd';
import Table, { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import React, { useCallback, useContext, useState } from 'react';
import TimeAgo from 'react-timeago'
import { Link } from 'react-router-dom';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useOrderServiceGetBackendv2Order } from '../../backendapi/queries';
import { OrderDTO, OrderlineService, SceneService, TemplatesService } from '../../backendapi/requests';
import { ConfigContext } from '../../contexts/ConfigContext';
import { StillImageConfiguration } from '../../models/StillImageConfiguration';
import { Orderline } from '../../openapi/requests';
import CreateTemplatePreset from '../templatePresets/CreateTemplatePreset';

var orderStatuses: { [key: number]: { icon: React.ReactElement, name: string } } = {
    0: { icon: <EditOutlined />, name: 'Draft' },
    10: { icon: <HourglassOutlined />, name: 'Placed' },
    20: { icon: <HourglassOutlined />, name: 'Delegating' },
    30: { icon: <LoadingOutlined spin />, name: 'Processing' },
    40: { icon: <StopOutlined />, name: 'Declined' },
    50: { icon: <DownloadOutlined />, name: 'Deliverable' },
    53: { icon: <ExclamationOutlined />, name: 'Delivery failed' },
    60: { icon: <CloudUploadOutlined />, name: 'Delivered' },
    99: { icon: <WarningOutlined />, name: 'Failed' },
}

const OrderList: React.FC = () => {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
    const { currentClient } = useContext(ConfigContext);
    const [messageApi, contextHolder] = message.useMessage();
    const [config, setConfig] = useState<string | undefined>(undefined);
    const [selectedTemplate, setSelectedTemplate] = useState<number | undefined>(undefined);
    const [createOpen, setCreateOpen] = useState(false);

    const { data, isLoading, refetch } = useOrderServiceGetBackendv2Order({
        clientId: currentClient.id,
        top: pageSize.toString(),
        skip: ((page - 1) * pageSize).toString(),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true',
        filter: 'clientId eq '+ currentClient.id
    });

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<OrderDTO> | SorterResult<OrderDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if (!Array.isArray(sorter)) {
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleCreateTemplate = useCallback(async (order: OrderDTO) => {
        setConfig(order.configuration ?? undefined);
        setSelectedTemplate(order.templateId ?? undefined);
        setCreateOpen(true);
    }, []);

    const handleUseAsDefault = useCallback(async (order: OrderDTO) => {
        var [orderline] = await OrderlineService.getBackendv2OrderOrderline(order.id, '1');

        if (orderline) {
            const template = await TemplatesService.getBackendv2Template1(orderline.templateId);
            const scene = await SceneService.getBackendv2Scene1(template.sceneId!);

            let config = JSON.parse(orderline.configurationSpec) as StillImageConfiguration;

            config.Cameras = undefined;

            scene.defaultConfigurationSpec = JSON.stringify(config);

            await SceneService.putBackendv2Scene(template.sceneId!, scene);
            messageApi.success("Scene updated");
        }
    }, [messageApi]);

    const columns: ColumnsType<OrderDTO> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: (value) => (
                <Link to={'/orders/' + value}>{value}</Link>
            ),
            sorter: (a, b) => (a.id) - (b.id),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => (
                <>{orderStatuses[value]?.icon}{' '}{orderStatuses[value]?.name}</>
            ),
            sorter: (a, b) => (a.status) - (b.status),
        },
        {
            title: 'User',
            dataIndex: 'orderedByUser',
            key: 'user',
            sorter: (a, b) => (a.status) - (b.status),
        },
        {
            title: 'Reference',
            dataIndex: 'orderReference',
            key: 'orderReference',
        },
        {
            title: 'Created',
            dataIndex: 'timestampCreate',
            key: 'created',
            render: (value) => (
                value && <TimeAgo date={value} />
            ),
            sorter: (a, b) => (new Date(a.timestampCreate)).getTime() - (new Date(b.timestampCreate)).getTime(),
        },
        {
            title: 'Delivered',
            dataIndex: 'timestampDelivery',
            key: 'delivery',
            render: (value) => (
                value && <TimeAgo date={value} />
            ),
            sorter: (a, b) => (new Date(a.timestampDelivery || '1900')).getTime() - (new Date(b.timestampDelivery || '1900')).getTime(),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Link to={'/orders/' + record.id}>Details</Link>
                    {record.projectId && <Link to={'/projects/' + record.projectId}>View Project</Link>}
                    <Popconfirm title="Are you sure?" onConfirm={() => handleUseAsDefault(record)}>
                        <Button type="link">Use as default for scene</Button>
                    </Popconfirm>
                    {<Button disabled={record.orderType !== 1} type="link" onClick={() => handleCreateTemplate(record)}>Create preset</Button>}
                </Space>
            ),
        },
    ];

    return (
        <>
            {contextHolder}
            <Space>
                <Button type="primary" onClick={() => refetch()} icon={<RedoOutlined />} style={{ marginBottom: 16 }}>
                    Refresh
                </Button>
            </Space>

            <CreateTemplatePreset config={config} isOpen={createOpen} onClose={() => setCreateOpen(false)} templateId={selectedTemplate} />

            <Table
                loading={isLoading}
                dataSource={data?.value}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                pagination={{
                    total: (data !== undefined && data['@odata.count']) ? data['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                }} />
        </>
    );
};

export default OrderList;